import { Injectable } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface BroadcastResult {
  successCount: number;
  failureCount: number;
  totalCount: number;
}

export type BroadcastSendFunction<T> = (phoneNumber: string, options: any) => Observable<T>;

@Injectable({
  providedIn: 'root'
})
export class WhatsAppBroadcastService {

  /**
   * Sends a broadcast message to multiple recipients
   * @param phoneNumbers - Array of phone numbers to send to
   * @param conversations - Array of conversation objects to look up template options
   * @param patientId - Patient ID for patient template
   * @param sendFn - Function that sends the message for a single phone number
   * @param destroy$ - Subject for unsubscribing
   * @returns Promise resolving to broadcast result
   */
  async sendBroadcast<T>(
    phoneNumbers: string[],
    conversations: any[],
    patientId: string | undefined,
    buildOptionsFn: (conversation: any) => any,
    sendFn: BroadcastSendFunction<T>,
    destroy$: Observable<any>
  ): Promise<BroadcastResult> {
    // Send to all selected conversations with appropriate template per conversation
    const sendPromises = phoneNumbers.map(phoneNumber => {
      const conversation = conversations.find(c => c.phoneNumber === phoneNumber);
      const options = buildOptionsFn(conversation);

      return firstValueFrom(
        sendFn(phoneNumber, options).pipe(takeUntil(destroy$))
      );
    });

    const results = await Promise.allSettled(sendPromises);
    const successCount = results.filter(r => r.status === 'fulfilled' && (r.value as any)?.success).length;
    const failureCount = results.length - successCount;

    return {
      successCount,
      failureCount,
      totalCount: phoneNumbers.length
    };
  }

  /**
   * Formats broadcast result message
   * @param result - Broadcast result
   * @param messageType - Type of message (e.g., 'message', 'PDF', 'voice note')
   * @returns Formatted message string
   */
  formatResultMessage(result: BroadcastResult, messageType: string): string {
    if (result.successCount === result.totalCount) {
      return `Broadcast ${messageType} sent successfully to ${result.successCount} conversation${result.successCount > 1 ? 's' : ''}.`;
    } else if (result.successCount > 0) {
      return `Broadcast ${messageType} sent to ${result.successCount} of ${result.totalCount} conversation${result.totalCount > 1 ? 's' : ''}. ${result.failureCount} failed.`;
    } else {
      return `Failed to send broadcast ${messageType} to all conversations.`;
    }
  }
}

